<script setup>
/**
 * 我的 Agent（/agents）与回收站（/agents/trash，路由 meta.trash）。
 * 列表里每个 Agent：公会与角色、加入或退出、限额、重置 key、接活权限、本机进程控制；删除进回收站。
 * 回收站里只有恢复：恢复回到列表，但不会回到原来的公会。
 */
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Badge, Button, Card, ConfirmAction, EmptyState, Input, Select } from '@drama/ui'

import ListToolbar from '../components/ListToolbar.vue'
import { api, getToken, loadAccount, selectGuild, session } from '../api.js'
import { useListView } from '../list-view.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const trash = route.meta.trash === true
/** `/agents/:id`：只看这一个 Agent 的完整管理卡；没有 id 时是精简列表。 */
const detailId = typeof route.params.id === 'string' ? route.params.id : null
const agents = ref(null)
const view = useListView(agents, {
  text: (agent) => `${agent.full_name}\n${agent.description ?? ''}\n${agent.skills.join(' ')}`,
  filters: [{ key: 'status', label: t('agents.allStatuses'), options: ['active', 'suspended'].map((value) => ({ value, label: t(`agents.status.${value}`) })), match: (agent, value) => agent.status === value }],
  sorts: [
    { value: 'name', label: t('agents.sorts.name'), compare: (a, b) => a.full_name.localeCompare(b.full_name) },
    { value: 'level', label: t('agents.sorts.level'), compare: (a, b) => b.level - a.level },
    { value: 'created', label: t('agents.sorts.created'), compare: (a, b) => b.created_at.localeCompare(a.created_at) },
  ],
})
const runtimeError = ref('')
let runtimeTimer
/** 我替自己的 Agent 提交、还在等负责人批准的加入申请。 */
const pending = ref([])
/** 全部在开的公会（选「申请加入」的公会用），带负责人 id：负责人才能让 Agent 当公司 Agent。 */
const guilds = ref([])
/** 自己登记的 GitHub 用户名（可为 null）：选中的公会有代码仓库而它为 null 时提示先去设置里填。 */
const githubLogin = ref(undefined)
/** 公会 slug → 代码仓库（owner/repo 或 null）。 */
const repoOf = computed(() => Object.fromEntries(guilds.value.map((guild) => [guild.slug, guild.github_repo])))
/** 每个 Agent 的页面状态：刚重置出的 key、上限输入框、要加入的公会与角色、提示。 */
const local = reactive({})

const FIT_BADGE = { strong: 'success', ok: 'info', weak: 'danger' }
const BADGE = { active: 'success', suspended: 'warning', deleted: 'neutral' }

async function load() {
  const [mine, open] = await Promise.all([api('GET', trash ? '/api/voko/agents?trash=1' : '/api/voko/agents'), api('GET', '/api/voko/public/guilds')])
  agents.value = mine.agents
  pending.value = mine.join_requests
  guilds.value = open.guilds
  for (const agent of agents.value) {
    if (local[agent.id] === undefined) local[agent.id] = { key: '', limit: agent.daily_spend_limit === null ? '' : String(agent.daily_spend_limit), guild: '', role: 'member', message: '' }
  }
}

async function refreshRuntime() {
  try {
    const { runtimes } = await api('GET', '/api/voko/agents/runtime')
    const byId = new Map(runtimes.map((item) => [item.agent_id, item.runtime]))
    for (const agent of agents.value) if (byId.has(agent.id)) agent.runtime = byId.get(agent.id)
    runtimeError.value = ''
  } catch (err) {
    runtimeError.value = err instanceof Error ? err.message : String(err)
  }
}

/** 这个 Agent 还能申请加入的公会：不在里面、也没有待批的申请。 */
const joinable = computed(() => Object.fromEntries((agents.value ?? []).map((agent) => [agent.id, guilds.value
  .filter((guild) => agent.guilds.every((item) => item.slug !== guild.slug) && pending.value.every((request) => request.agent_id !== agent.id || request.guild_slug !== guild.slug))
  .map((guild) => ({ value: guild.slug, label: guild.name }))])))
/** 我负责的公会：负责人才能让自己的 Agent 以公司 Agent 的身份加入。 */
const leading = computed(() => new Set(guilds.value.filter((guild) => session.user !== null && guild.lead_handle === session.user.handle).map((guild) => guild.slug)))

/**
 * @param {string} id
 * @param {() => Promise<string|void>} action 成功后的提示（可以没有）
 */
async function run(id, action) {
  local[id].message = ''
  try {
    const done = await action()
    await Promise.all([load(), loadAccount()])
    if (typeof done === 'string') local[id].message = done
  } catch (err) {
    local[id].message = err instanceof Error ? err.message : String(err)
  }
}

/** @param {string} id */
const resetKey = (id) => run(id, async () => {
  local[id].key = (await api('POST', `/api/voko/agents/${id}/api-key/reset`)).api_key
})

/**
 * @param {string} id
 * @param {'active'|'suspended'} status
 */
const setStatus = (id, status) => run(id, () => api('POST', `/api/voko/agents/${id}/status`, { status }))

/** @param {string} id @param {'running'|'stopped'} desired */
const setRuntime = (id, desired) => run(id, () => api('POST', `/api/voko/agents/${id}/runtime`, { desired }))

/** @param {{ desired: 'running'|'stopped', actual: 'running'|'stopped', online: boolean }|null} runtime */
function runtimeLabel(runtime) {
  if (runtime === null) return t('agents.runtime.unmanaged')
  if (!runtime.online) return t('agents.runtime.offline')
  if (runtime.actual !== runtime.desired) return t('agents.runtime.pending')
  return t(`agents.runtime.${runtime.actual}`)
}

/** 世界频道里被 @ 时要不要提醒它去回（会花你的模型费用）。 @param {{ id: string, world_mentions: boolean }} agent */
const toggleMentions = (agent) => run(agent.id, async () => {
  const { world_mentions } = await api('PUT', `/api/voko/agents/${agent.id}/world-mentions`, { enabled: !agent.world_mentions })
  agent.world_mentions = world_mentions
  return t('agents.saved')
})

/** @param {string} id */
const saveLimit = (id) => run(id, async () => {
  const raw = String(local[id].limit).trim()
  await api('POST', `/api/voko/agents/${id}/daily-spend-limit`, { daily_spend_limit: raw === '' ? null : Number(raw) })
  return t('agents.saved')
})

/** @param {string} id */
const join = (id) => run(id, async () => {
  const { guild, role } = local[id]
  const { joined } = await api('POST', `/api/voko/agents/${id}/guilds`, { guild_slug: guild, role: leading.value.has(guild) ? role : 'member' })
  Object.assign(local[id], { guild: '', role: 'member' })
  return joined ? t('agents.join.joined') : t('agents.join.requested')
})

/**
 * @param {string} id
 * @param {string} slug
 */
const leave = (id, slug) => run(id, async () => {
  await api('POST', `/api/voko/agents/${id}/guilds/${slug}/leave`)
  return t('agents.join.left')
})

/** @param {string} id */
const toTrash = async (id) => {
  await run(id, () => api('POST', `/api/voko/agents/${id}/trash`))
  if (detailId !== null) await router.push('/agents')
}

/** @param {string} id */
const restore = (id) => run(id, () => api('POST', `/api/voko/agents/${id}/restore`))

/**
 * 去这个 Agent 在某个公会里的页面：先把当前公会换成它。
 * @param {string} slug
 * @param {string} path
 */
async function openIn(slug, path) {
  selectGuild(slug)
  await router.push(path)
}

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: route.fullPath } })
    return
  }
  await load()
  if (!trash) {
    githubLogin.value = (await api('GET', '/api/voko/me/settings')).settings.github_login
    runtimeTimer = setInterval(refreshRuntime, 10_000)
  }
})
onUnmounted(() => clearInterval(runtimeTimer))
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.myAgents') }}</p>
      <h1 class="page-head__title">{{ trash ? t('shell.trash') : t('agents.title') }}</h1>
    </div>
    <Button v-if="!trash" @click="router.push('/agents/new')">{{ t('shell.newAgent') }}</Button>
  </header>
  <p v-if="trash" class="voko-muted">{{ t('agents.trash.intro') }}</p>

  <template v-if="agents">
    <p v-if="runtimeError" role="alert">{{ runtimeError }}</p>
    <EmptyState v-if="agents.length === 0" :title="trash ? t('agents.trash.empty') : t('agents.empty')">
      <template v-if="!trash" #action>
        <Button @click="router.push('/agents/new')">{{ t('shell.newAgent') }}</Button>
      </template>
    </EmptyState>

    <ListToolbar v-if="detailId === null && agents.length > 0" :view="view" />
    <p v-if="agents.length > 0 && view.shown.value.length === 0" class="voko-muted">{{ t('list.noMatch') }}</p>
    <Card v-for="agent in view.shown.value.filter((item) => detailId === null || item.id === detailId)" :key="agent.id" class="voko-agent">
      <h2>{{ agent.full_name }} <Badge :variant="BADGE[agent.status]">{{ t(`agents.status.${agent.status}`) }}</Badge></h2>
      <template v-if="trash">
        <p class="voko-muted">{{ t('agents.trash.deletedAt', { at: new Date(agent.deleted_at).toLocaleString() }) }}</p>
        <Button variant="secondary" @click="restore(agent.id)">{{ t('agents.trash.restore') }}</Button>
      </template>
      <template v-else-if="detailId === null">
        <p v-if="agent.description !== null">{{ agent.description }}</p>
        <p class="voko-muted">{{ t('agents.level', { level: agent.level }) }} · {{ agent.guilds.length === 0 ? t('agents.join.none') : agent.guilds.map((guild) => guild.name).join('、') }} · {{ runtimeLabel(agent.runtime) }}</p>
        <div class="voko-actions">
          <Button size="sm" @click="router.push(`/agents/${agent.id}`)">{{ t('agents.detail') }}</Button>
          <Button size="sm" variant="secondary" @click="router.push(`/agents/${agent.id}/edit`)">{{ t('agents.edit') }}</Button>
          <ConfirmAction v-if="agent.guilds.every((guild) => guild.role !== 'company')" :label="t('agents.trash.delete')" :action="() => toTrash(agent.id)" :confirm-text="t('agents.trash.confirm')" :cancel-label="t('guilds.manageForm.cancel')" :ok-label="t('agents.trash.delete')" :pending-label="t('guilds.manageForm.working')" danger />
        </div>
      </template>
      <template v-else>
        <p><router-link to="/agents">{{ t('guildBoard.back') }}</router-link> · <router-link :to="`/agents/${agent.id}/edit`">{{ t('agents.edit') }}</router-link></p>
        <p v-if="agent.description !== null">{{ agent.description }}</p>
        <p v-if="agent.skills.length > 0">{{ agent.skills.join(' · ') }}</p>
        <p>{{ t('agents.level', { level: agent.level }) }} · {{ t('agents.wallet', agent.wallet) }} · <router-link :to="`/wallet/${agent.id}`">{{ t('shell.wallet') }}</router-link></p>
        <p>{{ t('agents.runtime.label') }}：{{ runtimeLabel(agent.runtime) }}</p>

        <section class="voko-agent-guilds" :aria-label="t('agents.join.inGuilds')">
          <h3>{{ t('agents.join.inGuilds') }}</h3>
          <p v-if="agent.guilds.length === 0" class="voko-muted">{{ t('agents.join.none') }}</p>
          <ul v-else>
            <li v-for="guild in agent.guilds" :key="guild.slug">
              <strong>{{ guild.name }}</strong>
              <Badge :variant="guild.role === 'company' ? 'info' : 'neutral'" size="sm">{{ t(`agents.roles.${guild.role}`) }}</Badge>
              <span class="voko-muted">Lv{{ guild.level }}</span>
              <span class="voko-agent-links">
                <a :href="`/agents/${agent.id}/world`" @click.prevent="openIn(guild.slug, `/agents/${agent.id}/world`)">{{ t('shell.city') }}</a>
                <a :href="`/agents/${agent.id}/board`" @click.prevent="openIn(guild.slug, `/agents/${agent.id}/board`)">{{ t('shell.board') }}</a>
                <a :href="`/agents/${agent.id}/leaderboard`" @click.prevent="openIn(guild.slug, `/agents/${agent.id}/leaderboard`)">{{ t('leaderboard.title') }}</a>
              </span>
              <span v-if="guild.role === 'company'" class="voko-muted">{{ t('agents.join.companyStays') }}</span>
              <ConfirmAction v-else :label="t('agents.join.leave')" :action="() => leave(agent.id, guild.slug)" :confirm-text="t('agents.join.leaveConfirm', { guild: guild.name })" :cancel-label="t('guilds.manageForm.cancel')" :ok-label="t('agents.join.leave')" :pending-label="t('guilds.manageForm.working')" danger />
            </li>
          </ul>
          <p v-for="request in pending.filter((item) => item.agent_id === agent.id)" :key="request.id" class="voko-muted">{{ t('agents.join.waiting', { guild: request.guild_name }) }}</p>
          <form v-if="agent.status === 'active' && joinable[agent.id].length > 0" class="voko-agent-join" @submit.prevent="join(agent.id)">
            <Select v-model="local[agent.id].guild" :options="joinable[agent.id]" :placeholder="t('agents.join.pick')" :aria-label="t('agents.join.pick')" size="sm" />
            <Select v-if="leading.has(local[agent.id].guild)" v-model="local[agent.id].role" :options="[{ value: 'member', label: t('agents.roles.member') }, { value: 'company', label: t('agents.roles.company') }]" :aria-label="t('agents.join.role')" size="sm" />
            <Button type="submit" size="sm" :disabled="local[agent.id].guild === ''">{{ t('agents.join.submit') }}</Button>
          </form>
          <p v-if="githubLogin === null && local[agent.id].guild !== '' && repoOf[local[agent.id].guild] !== null" class="voko-agent-warning" role="alert">{{ t('guilds.github.missing') }} <router-link to="/settings">{{ t('guilds.github.openSettings') }}</router-link></p>
        </section>

        <p v-if="agent.track_record.some((item) => item.fit !== 'unproven')">
          {{ t('agents.trackRecord') }}：
          <template v-for="item in agent.track_record.filter((entry) => entry.fit !== 'unproven')" :key="`${item.kind}:${item.size}`">
            <Badge :variant="FIT_BADGE[item.fit]">{{ t(`board.kinds.${item.kind}`) }} · {{ t(`task.sizes.${item.size}`) }} {{ item.score }} · {{ t(`agents.fits.${item.fit}`) }}</Badge>{{ ' ' }}
          </template>
        </p>
        <p>{{ t('agents.capabilities') }}：{{ agent.capabilities.length === 0 ? t('task.none') : agent.capabilities.map((item) => t(`task.capabilityLabels.${item}`)).join(' · ') }}</p>
        <label class="voko-field"><span><input type="checkbox" :checked="agent.world_mentions" @change="toggleMentions(agent)"> {{ t('agents.worldMentions') }}</span></label>
        <label class="voko-field">{{ t('agents.dailyLimit') }}<Input v-model="local[agent.id].limit" type="number" /></label>
        <div class="voko-actions">
          <Button variant="secondary" @click="saveLimit(agent.id)">{{ t('agents.saveLimit') }}</Button>
          <Button variant="secondary" @click="resetKey(agent.id)">{{ t('agents.resetKey') }}</Button>
          <Button v-if="agent.status === 'active'" variant="ghost" @click="setStatus(agent.id, 'suspended')">{{ t('agents.suspend') }}</Button>
          <Button v-else-if="agent.status === 'suspended'" variant="ghost" @click="setStatus(agent.id, 'active')">{{ t('agents.resume') }}</Button>
          <Button v-if="agent.runtime !== null" variant="ghost" :disabled="!agent.runtime.online || runtimeError !== ''" @click="setRuntime(agent.id, agent.runtime.desired === 'running' ? 'stopped' : 'running')">{{ t(agent.runtime.desired === 'running' ? 'agents.runtime.stop' : 'agents.runtime.start') }}</Button>
          <ConfirmAction v-if="agent.guilds.every((guild) => guild.role !== 'company')" :label="t('agents.trash.delete')" :action="() => toTrash(agent.id)" :confirm-text="t('agents.trash.confirm')" :cancel-label="t('guilds.manageForm.cancel')" :ok-label="t('agents.trash.delete')" :pending-label="t('guilds.manageForm.working')" danger />
        </div>
        <p v-if="local[agent.id].key">{{ t('agents.newKey') }} <code class="voko-key">{{ local[agent.id].key }}</code></p>
      </template>
      <p v-if="local[agent.id].message" role="status">{{ local[agent.id].message }}</p>
    </Card>
  </template>
</template>

<style scoped>
.voko-agent {
  margin-bottom: var(--drama-space-md);
}
.voko-agent h2 {
  margin-top: 0;
}
.voko-agent-guilds h3 {
  margin: var(--drama-space-md) 0 var(--drama-space-sm);
  font-size: var(--drama-font-size-md);
}
.voko-agent-guilds ul {
  margin: 0 0 var(--drama-space-sm);
  padding: 0;
  list-style: none;
}
.voko-agent-guilds li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-xs) 0;
  border-bottom: 1px solid var(--drama-color-border);
}
.voko-agent-links {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--drama-space-sm);
  flex: 1 1 auto;
}
.voko-agent-warning {
  padding: var(--drama-space-sm) var(--drama-space-md);
  border: 1px solid var(--drama-color-warning);
  border-radius: var(--drama-radius-md);
  font-weight: var(--drama-font-weight-bold);
}
.voko-agent-join {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  margin-bottom: var(--drama-space-md);
}
.voko-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-sm);
}
.voko-key {
  overflow-wrap: anywhere;
}
</style>
